<div class="page-content-wrapper">
  <app-sidebar-layout [showMenuToggle]="true">
    <div class="dashboard-container">
      <div class="table-header">
        <div class="card-main-title">
          <div class="dashboard-tab">
            <span class="tab-label">{{ 'Dashboard' | translate }}</span>
          </div>
        </div>
        @if (!canManage && currentView?.isShared) {
          <span class="shared-readonly-stamp">
            <i class="material-icons">lock</i>
            {{ 'Shared - read-only' | translate }}
          </span>
        }
        <div class="table-actions">
          <div class="d-flex align-items-center ml-auto">
            <div class="mode-toggle-pill">
              <button type="button" class="toggle-option" [class.active]="!isEditMode"
                (click)="isEditMode && toggleEditMode()">
                <i class="material-icons">dashboard</i>
                <span>{{ 'View' | translate }}</span>
              </button>
              <button type="button" class="toggle-option" [class.active]="isEditMode"
                [disabled]="!canManage"
                (click)="!isEditMode && toggleEditMode()">
                <i class="material-icons">edit</i>
                <span>{{ 'Edit' | translate }}</span>
              </button>
            </div>
            <div class="sort-field table-view-selector">
              <app-dashboard-view-selector [organizationLevelId]="organizationLevelId"
                [selectedViewId]="$safeNavigationMigration(currentView?.id)" [defaultViewId]="defaultViewId"
                [currentViewName]="$safeNavigationMigration(currentView?.viewName)" (viewSelected)="onViewSelected($event)"
                (addView)="onAddView($event)" (viewsChanged)="onViewsChanged($event)"
                (defaultViewChanged)="onDefaultViewChanged($event)"
                (viewDuplicated)="onViewDuplicated($event)">
              </app-dashboard-view-selector>
            </div>
            @if (canManage) {
              <div class="sort-field grid-settings">
                <button type="button" class="btn" [title]="'Dashboard settings' | translate"
                  (click)="openSettings()">
                  <img src="assets/images/grid-settings.svg" alt="dashboard settings"
                    class="grid-settings-icon" />
                  </button>
                </div>
              }
            </div>
          </div>
        </div>

        <div class="dashboard-content">
          @if (visibleWidgets.length > 0) {
            <div class="dashboard-grid" [class.dragging]="isDragging"
              [class.resizing]="isResizing" [class.edit-mode]="isEditMode" [style.--columns]="gridColumns"
              [style.--row-height]="layoutConfig.rowHeight + 'vh'" [style.--gap]="layoutConfig.gap + 'px'">
              @for (slot of gridSlots; track trackBySlotIndex($index, slot)) {
                @if (!slot.isOccupiedBySpan) {
                  <div class="widget-cell"
                    [class.drag-over]="dragOverSlotIndex === slot.index" [class.empty-slot]="!slot.widget"
                    [class.is-resize-widget]="slot.widget && isResizing && resizeWidget?.id === slot.widget.id"
                    [style.grid-column-start]="slot.widget ? slot.widget.positionX + 1 : slot.x + 1"
                    [style.grid-column-end]="slot.widget ? 'span ' + (slot.widget.width || 1) : null"
                    [style.grid-row-start]="slot.widget ? slot.widget.positionY + 1 : slot.y + 1"
                    [style.grid-row-end]="slot.widget ? 'span ' + (slot.widget.height || 1) : null"
                    (dragover)="onDragOver($event, slot.index)" (dragleave)="onDragLeave()"
                    (drop)="onDrop($event, slot.index)">
                    @if (slot.widget) {
                      <div class="widget-wrapper" [attr.draggable]="isEditMode && canManage"
                        [class.is-resizing]="isResizing && resizeWidget?.id === slot.widget.id"
                        (dragstart)="onDragStart($event, slot.index)" (dragend)="onDragEnd()">
                        @if (isEditMode && canManage) {
                          <div class="widget-header-actions">
                            <button type="button" class="widget-action-btn"
                              (click)="openWidgetSettings(slot.widget)" [title]="'Settings' | translate">
                              <i class="material-icons">settings</i>
                            </button>
                            <button type="button" class="widget-action-btn widget-remove-btn"
                              (click)="removeWidget(slot.widget)" [title]="'Remove' | translate">
                              <i class="material-icons">delete</i>
                            </button>
                            @if (isPersistedWidget(slot.widget.id) && dirtyWidgetIds.has(slot.widget.id)) {
                              <button type="button" class="widget-action-btn widget-save-btn widget-save-spaced"
                                (click)="saveWidget(slot.widget)" [title]="'Save' | translate">
                                <i class="material-icons">save</i>
                              </button>
                            }
                            @if (dirtyWidgetIds.has(slot.widget.id)) {
                              <span class="widget-temp-badge">
                                <i class="material-icons">schedule</i>
                                {{ 'Temporary View' | translate }}
                              </span>
                            }
                          </div>
                        }
                        @switch (resolveWidgetTypeKey(slot.widget)) {
                          @case ('grid-view') {
                            <app-grid-widget
                              [widgetConfig]="slot.widget.configJson" [widgetId]="slot.widget.id"
                            [widgetTitle]="widgetTitleByWidgetId[slot.widget.id]"
                            [titleMeta]="widgetTitleMetaByWidgetId[slot.widget.id] || emptyTitleMeta"></app-grid-widget>
                          }
                          @case ('sample-status-pipeline') {
                            <app-sample-status-widget
                              [widgetConfig]="slot.widget.configJson" [widgetId]="slot.widget.id"
                              [widgetTitle]="widgetTitleByWidgetId[slot.widget.id]"
                              [titleMeta]="widgetTitleMetaByWidgetId[slot.widget.id] || emptyTitleMeta"
                            (configChange)="onWidgetConfigured({ widgetId: slot.widget.id, config: $event })"></app-sample-status-widget>
                          }
                          @case ('exceedance-compliance') {
                            <app-exceedance-compliance-widget
                              [widgetConfig]="slot.widget.configJson" [widgetId]="slot.widget.id"
                              [widgetTitle]="widgetTitleByWidgetId[slot.widget.id]"
                              [titleMeta]="widgetTitleMetaByWidgetId[slot.widget.id] || emptyTitleMeta"
                            (configChange)="onWidgetConfigured({ widgetId: slot.widget.id, config: $event })"></app-exceedance-compliance-widget>
                          }
                          @case ('equipment-calibration-status') {
                            <app-equipment-calibration-status-widget
                              [widgetConfig]="slot.widget.configJson" [widgetId]="slot.widget.id"
                              [widgetTitle]="widgetTitleByWidgetId[slot.widget.id]"
                              [titleMeta]="widgetTitleMetaByWidgetId[slot.widget.id] || emptyTitleMeta"
                            (configChange)="onWidgetConfigured({ widgetId: slot.widget.id, config: $event })"></app-equipment-calibration-status-widget>
                          }
                          @case ('survey-progress') {
                            <app-survey-progress-widget
                              [widgetConfig]="slot.widget.configJson" [widgetId]="slot.widget.id"
                              [widgetTitle]="widgetTitleByWidgetId[slot.widget.id]"
                              [titleMeta]="widgetTitleMetaByWidgetId[slot.widget.id] || emptyTitleMeta"
                            (configChange)="onWidgetConfigured({ widgetId: slot.widget.id, config: $event })"></app-survey-progress-widget>
                          }
                          @case ('sample-collection-metrics') {
                            <app-sample-collection-metrics-widget
                              [widgetConfig]="slot.widget.configJson" [widgetId]="slot.widget.id"
                              [widgetTitle]="widgetTitleByWidgetId[slot.widget.id]"
                              [titleMeta]="widgetTitleMetaByWidgetId[slot.widget.id] || emptyTitleMeta"
                            (configChange)="onWidgetConfigured({ widgetId: slot.widget.id, config: $event })"></app-sample-collection-metrics-widget>
                          }
                          @case ('seg-profile-summary') {
                            <app-seg-profile-summary-widget
                              [widgetConfig]="slot.widget.configJson" [widgetId]="slot.widget.id"
                              [widgetTitle]="widgetTitleByWidgetId[slot.widget.id]"
                              [titleMeta]="widgetTitleMetaByWidgetId[slot.widget.id] || emptyTitleMeta"
                            (configChange)="onWidgetConfigured({ widgetId: slot.widget.id, config: $event })"></app-seg-profile-summary-widget>
                          }
                          @default {
                            <div class="unknown-widget">
                              <i class="material-icons">error_outline</i>
                              <span>{{ 'Unknown widget' | translate }}</span>
                            </div>
                          }
                        }
                        @if (isEditMode && canManage) {
                          <div class="widget-resize-handle resize-se"
                            (mousedown)="onResizeStart($event, slot.widget, 'se')"
                            [title]="'Drag to resize' | translate">
                            <i class="material-icons">open_in_full</i>
                          </div>
                        }
                        @if (isEditMode && canManage) {
                          <div class="widget-resize-handle resize-sw"
                            (mousedown)="onResizeStart($event, slot.widget, 'sw')"
                            [title]="'Drag to resize' | translate">
                            <i class="material-icons">open_in_full</i>
                          </div>
                        }
                        @if (isEditMode && canManage) {
                          <div class="widget-resize-handle resize-ne"
                            (mousedown)="onResizeStart($event, slot.widget, 'ne')"
                            [title]="'Drag to resize' | translate">
                            <i class="material-icons">open_in_full</i>
                          </div>
                        }
                        @if (isEditMode && canManage) {
                          <div class="widget-resize-handle resize-nw"
                            (mousedown)="onResizeStart($event, slot.widget, 'nw')"
                            [title]="'Drag to resize' | translate">
                            <i class="material-icons">open_in_full</i>
                          </div>
                        }
                      </div>
                    } @else {
                      @if (isEditMode) {
                        <div class="empty-slot-placeholder" (click)="onEmptySlotClick(slot)">
                          <i class="material-icons">add_circle_outline</i>
                          <span>{{ 'Add or Resize Widget' | translate }}</span>
                        </div>
                      }
                    }
                  </div>
                }
              }
            </div>
          }

          @if (visibleWidgets.length === 0) {
            <div class="empty-dashboard">
              <i class="material-icons">dashboard_customize</i>
              <h3>{{ 'Your dashboard is empty' | translate }}</h3>
              <p>{{ 'Add widgets below or by adding them in settings' | translate }}</p>
              @if (canManage) {
                <button type="button" class="add-widgets-btn" (click)="openSettings('widgets')">
                  <i class="material-icons">add</i>
                  <span>{{ 'Add Widgets' | translate }}</span>
                </button>
              }
            </div>
          }
        </div>
      </div>
    </app-sidebar-layout>
  </div>

  <app-dashboard-settings-flyout [isOpen]="isSettingsOpen" [dashboardView]="currentView" [activeTab]="settingsActiveTab"
    [selectedWidgetInput]="selectedWidgetForSettings" [targetSlotIndex]="targetSlotIndex"
    [defaultOrgLevelId]="organizationLevelId" [hasPendingChanges]="hasPendingChanges" (close)="closeSettings($event)"
    (widgetsChanged)="onWidgetsChanged($event)" (layoutChanged)="onLayoutChanged($event)"
    (widgetAdded)="onWidgetAdded($event)" (widgetRemoved)="onWidgetRemoved($event)"
    (widgetConfigured)="onWidgetConfigured($event)" (widgetTitleChanged)="onWidgetTitleChanged($event)"
    (resetToDefault)="onResetToDefault()">
  </app-dashboard-settings-flyout>